import {
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AttributionService } from 'src/app/services/attribution.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LoginValidationErrors } from 'src/app/interfaces/login-validation-errors.interface';
import { PlansService } from 'src/app/services/plans.service';
import { Plan } from 'src/app/plans/plan-type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataRefreshService } from 'src/app/services/data-refresh.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { DomSanitizer } from '@angular/platform-browser';
import { ResponseStatus } from 'src/app/types/auth-types';
import {
  MatCheckbox,
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GenerateIdentifierDirective } from 'src/app/directives/generate-identifier.directive';
import { FooterComponent } from '../footer/footer.component';
import { ButtonComponent } from '../ui/button/button.component';
import { CardComponent } from '../ui/card/card.component';
import { LoadingSpinnerComponent } from '../ui/loading-spinner/loading-spinner.component';

enum AuthError {
  OAUTH_FAILED = 'OAUTH_FAILED',
}

export const passwordMatchValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const password = control.get('password');
  const confirmPassword = control.get('confirmPassword');

  if (!password || !confirmPassword) return null;

  return password.value === confirmPassword.value
    ? null
    : { passwordMismatch: true };
};

export const emailMatchValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const email = control.get('email');
  const confirmEmail = control.get('confirmEmail');

  if (!email || !confirmEmail) return null;

  return email.value === confirmEmail.value ? null : { emailMismatch: true };
};

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatCheckbox,
    ButtonComponent,
    CardComponent,
    FooterComponent,
    GenerateIdentifierDirective,
    MatTooltipModule,
    LoadingSpinnerComponent,
  ],
})
export class LoginComponent implements OnInit {
  isLoading: boolean = false;
  dataRefreshService = inject(DataRefreshService);
  loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', Validators.required),
  });

  // Note: there's no "confirm email" input in the template (removed at some point without
  // updating this form), so signupForm intentionally has no confirmEmail control/validator
  // — a required-but-unfillable control would leave the form (and the submit button) stuck
  // invalid forever regardless of what the visible fields contain.
  signupForm = new FormGroup(
    {
      firstName: new FormControl('', Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
      ]),
      confirmPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
      ]),
    },
    { validators: [passwordMatchValidator] },
  );

  formError = '';
  isSubmitted: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;
  loginValidationErrors: LoginValidationErrors = {};
  planSelected: boolean = false;
  selectedPlan: Plan | null = null;
  emailSignUp: boolean = false;
  @ViewChild('loginContainer', { static: false }) containerRef!: ElementRef;
  @ViewChild('signupContainer', { static: false })
  signupContainerRef!: ElementRef;
  sanitiser = inject(DomSanitizer);
  toastrSvc = inject(ToastrService);
  //option to extend this with more error messages from socialite providers/more specific
  error_messages: Record<AuthError, string> = {
    [AuthError.OAUTH_FAILED]:
      'Something went wrong with the login process. Please try again.',
  };
  optInForMarketing: boolean = false;

  private destroyRef = inject(DestroyRef);
  private attribution = inject(AttributionService);

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private planService: PlansService,
  ) {
    this.activeRoute.queryParams.subscribe((params) => {
      const action = params['action'];
      const plan = params['plan'];
      const error = params['error'] as AuthError | undefined;

      if (action === 'signup') {
        this.showSignupPage();
      }

      this.planSelected = !!plan;
      if (this.planSelected) {
        this.getPlanById(plan);
      }

      if (error && this.error_messages[error]) {
        this.displayErrorToast(this.error_messages[error]);
      }
    });

    const returnUrl = this.activeRoute.snapshot.queryParams['returnUrl'];
    if (returnUrl) {
      this.authenticationService.saveReturnUrl(returnUrl);
    }

    const action = this.activeRoute.snapshot.queryParams['action'];
    const isSignupFlow = action === 'signup';

    if (this.authenticationService.isUserLoggedIn() && !isSignupFlow) {
      if (!this.authenticationService.hasSubscribe(this.router.url)) {
        this.router.navigateByUrl('/membership');
        return;
      }
      this.router.navigateByUrl('/dashboard');
      return;
    }
  }

  ngOnInit(): void {
    this.loginForm.valueChanges.subscribe(() => {
      this.formError = '';
    });
  }

  getPlanById(planId: number) {
    this.planService
      .getPlan(planId, true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          let firstPoint = '';

          if (data?.description) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(
              data.description as string,
              'text/html',
            );

            const firstLi = doc.querySelector('li');
            firstPoint =
              firstLi?.textContent?.trim() ??
              doc.querySelector('p')?.textContent?.trim() ??
              '';
          }

          this.selectedPlan = {
            ...data,
            description: firstPoint
              ? this.sanitiser.bypassSecurityTrustHtml(firstPoint)
              : '',
          };
        },

        error: (err) => {
          //console.log('Error fetching plan:', err);
          this.removePlanQueryParam();
        },
      });
  }

  removePlanQueryParam() {
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { plan: null },
      queryParamsHandling: 'merge',
    });
  }

  login() {
    this.clearError();
    this.isSubmitted = true;
    const email = this.loginForm.get('email')?.value;
    const password = this.loginForm.get('password')?.value;
    if (this.loginForm.invalid || !email || !password) {
      return;
    }
    this.isLoading = true;
    this.authenticationService
      .authenticate(email, password, this.selectedPlan?.id)
      .subscribe({
        next: async (res) => {
          // The `finally` below ensures that the spinner stops regardless of what happens in this block.
          try {
            if (!res.token) {
              this.formError = "We couldn't log you in — please try again.";
              return;
            }
            this.authenticationService.storeToken(res.token);

            const { user } = await this.dataRefreshService.refreshAll();
            if (!user) {
              this.formError = "We couldn't load your account. Please try again.";
              return;
            }

            await this.authenticationService.navigateAfterAuth(this.selectedPlan?.id);
          } catch {
            this.formError = 'Something went wrong. Please try again.';
          } finally {
            this.isLoading = false;
          }
        },
        error: (error) => {
          if (error.status === ResponseStatus.WARNING) {
            this.handleValidationWarning(error);
          } else {
            this.formError = error.message;
          }
          this.isLoading = false;
        },
      });
  }

  register() {
    this.clearError();
    // register() sends this via HttpParams.fromObject (form-encoded), which doesn't tolerate
    // undefined values — spread it in only when there actually is a plan, rather than always
    // including a possibly-undefined plan_id key. The backend independently re-validates
    // whatever it receives (integer + exists in tlh_prices) before recording it.
    const planId = this.selectedPlan?.id;
    const body = {
      first_name: this.signupForm.get('firstName')?.value,
      last_name: '',
      email: this.signupForm.get('email')?.value,
      password: this.signupForm.get('password')?.value,
      accept_marketing: this.optInForMarketing,
      accept: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(planId ? { plan_id: planId } : {}),
      ...this.attribution.get(),
    };
    this.isLoading = true;
    this.authenticationService.register(body).subscribe({
      next: async (res: any) => {
        try {
          if (!res.token) {
            this.formError = 'Something went wrong finishing your sign up. Please try again.';
            return;
          }
          this.authenticationService.storeToken(res.token);

          const { user } = await this.dataRefreshService.refreshAll();
          if (!user) {
            this.formError = "We couldn't load your account. Please try again.";
            return;
          }

          await this.authenticationService.navigateAfterAuth(this.selectedPlan?.id);
        } catch {
          this.formError = 'Something went wrong. Please try again.';
        } finally {
          this.isLoading = false;
        }
      },
      error: (error) => {
        if (error?.status === ResponseStatus.WARNING) {
          this.handleValidationWarning(error);
        } else {
          this.formError = error?.message ?? 'Something went wrong signing you up. Please try again.';
        }
        this.isLoading = false;
      },
    });
  }

  handleValidationWarning(res: any) {
    if (res.data) {
      const messages = Object.values(res.data).flat().join(' ');
      this.formError = messages || res.message;
    } else {
      this.formError = res?.message;
    }
  }

  clearError() {
    this.formError = '';
    this.loginValidationErrors = {};
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  navigateToMemberships() {
    // window.location.href = 'https://thelightuphub.com/membership';
    this.router.navigateByUrl('/membership');
  }

  showSignupPage() {
    this.clearError();
    setTimeout(() => {
      this.containerRef?.nativeElement.classList.add('right-panel-active');
      // add signup to url without reloading
      this.router?.navigate([], {
        relativeTo: this.activeRoute,
        queryParams: { action: 'signup' },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }, 0);
  }

  showLoginPage() {
    this.clearError();
    this.containerRef?.nativeElement.classList.remove('right-panel-active');
    // add signup to url without reloading
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { action: 'login' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  googleSignIn() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    const queryParams = this.activeRoute.snapshot.queryParams;

    sessionStorage.setItem('oauth_state', JSON.stringify({
      plan: queryParams['plan'] ?? null,
    }));

    const params = new URLSearchParams({
      opt_in:   String(this.optInForMarketing ?? false),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    Object.entries(this.attribution.get()).forEach(([key, val]) => {
      params.set(key, val);
    });

    this.redirectToGoogleAuth(`${environment.apiUrl}/api/v2/auth/google?${params.toString()}`);
  }

  // Extracted so tests can spy on the redirect without a real window.location navigation.
  private redirectToGoogleAuth(url: string): void {
    window.location.href = url;
  }

  googleSignUp() {
    this.googleSignIn();
  }

  appleSignIn() {
    alert('signup coming soon');
  }
  appleSignUp() {
    this.appleSignIn(); // TODO
  }

  showSignupForm() {
    // show a signup form and hide social signup buttons
    this.signupContainerRef.nativeElement.classList.add('email-signup-active');
    this.emailSignUp = true;
    // put cursor focus on name field
    setTimeout(() => {
      const firstNameField = document.getElementById('firstName');
      if (firstNameField) {
        firstNameField.focus();
      }
    }, 100);
  }

  hideSignupForm() {
    // show a signup form and hide social signup buttons
    this.signupContainerRef.nativeElement.classList.remove(
      'email-signup-active',
    );
    this.emailSignUp = false;
  }

  displayErrorToast(error: string) {
    this.toastrSvc.show(
      `<div class="toast-content">
        <img src="assets/images/red-alert.png" class="toast-icon" />
        <div class="toast-text">
          <strong>Something went wrong</strong><br>
          <span class="subtitle-text">${error}. Please try again.</span>
        </div>
      </div>`,
      '',
      {
        toastClass: 'custom-toast-container',
        enableHtml: true,
        timeOut: 3000,
      },
    );
    //Clear the param from URL without reloading
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams: { error: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  forgotPassword() {
    this.router.navigateByUrl('/forgot-password');
  }

  showPlans() {
    this.router.navigateByUrl('/membership');
  }

  navigateToHome() {
    window.location.href = 'https://thelightuphub.com';
  }

  onCheckboxChange(value: MatCheckboxChange) {
    this.optInForMarketing = value.checked;
  }
}
